/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import Input from "./Input";
import KeyInput from "../KeyInput";
import OptionInput from "./OptionInput";
import Errors from "../../../Components/Errors";
import CompoundInput from "./CompoundInput";

interface Option {
  Type: string;
  Value?: any;
  Data?: any;
  Key?: string;
  Order?: number;
  ComponentConfig?: any;
}

interface Condition {
  UUID?: string;
  Type?: string;
  Options?: Option[];
  Operator?: string;
  [key: string]: any;
}

interface InputConfig {
  type: "property" | "option" | "compound" | "heading" | "html";
  field: string;
  label?: string;
  props?: any;
  optionDefaults?: (values: any[]) => Record<string, any>;
  propertyDefaults?: (values: any[]) => Record<string, any>;
  ifOptionTypeHas?: Array<{ optionType: string; values: any[] }>;
  ifPropertyHas?: Array<{ field: string; values: any[] }>;
  isDataCategory?: boolean;
  defaultValue?: any;
  children?: React.ReactNode;
  html?: React.ReactNode;
}

interface CommonInputProps {
  isConditionInput?: boolean;
  input: InputConfig;
  condition: Condition;
  setCondition?: (condition: Condition) => void;
  className?: string;
}

export default function CommonInput({
  isConditionInput = false,
  input,
  condition,
  setCondition,
  className,
}: CommonInputProps) {
  const optionChangeHandler = ({ values }: { values: Option[] }) => {
    const val = values.filter((v) => v.Type === input.field);
    // .map((v) => v.Value);

    const optionDefaults = input.optionDefaults?.(val);
    const propertyDefaults = input.propertyDefaults?.(val);

    setCondition?.({
      ...condition,
      ...propertyDefaults,
      Options: [
        ...values.filter(
          (v) => !Object.keys(optionDefaults || {}).includes(v.Type)
        ),
        ...(Object.keys(optionDefaults || {})
          .filter((key) => optionDefaults?.[key] !== null)
          .reduce((acc: Option[], key) => {
            // if optionDefaults[key] is an object
            if (typeof optionDefaults?.[key] === "object") {
              if (Array.isArray(optionDefaults[key])) {
                // if it's an array, spread it
                return [
                  ...acc,
                  ...optionDefaults[key].map((v: any) => ({ Type: key, ...v })),
                ];
              }
              // else spread the object
              return [...acc, { Type: key, ...optionDefaults[key] }];
            }

            // if optionDefaults[key] is a string, use it as Value)
            return [
              ...acc,
              {
                Type: key,
                Value: optionDefaults?.[key],
              },
            ];
          }, []) || []),
      ],
    });
  };

  const propertyChangeHandler = (values: any[]) => {
    const optionDefaults = input.optionDefaults?.(values);
    const propertyDefaults = input.propertyDefaults?.(values);
    // reset options when operator changes
    if (
      input.field === "Operator" &&
      (condition.Type === "form:reply" || condition.Type === "form-validation")
    ) {
      setCondition?.({
        ...condition,
        [input.field]: values[0],
        Options: condition.Options?.filter(
          (opt: Option) => opt.Type === "question" || opt.Type === "BooleanOperator"
        ),
      });
      return;
    }
    setCondition?.({
      ...condition,
      [input.field]:
        input.props?.type === "number" ? Number(values[0]) : values[0],
      ...propertyDefaults,
      Options: [
        ...(condition.Options?.filter(
          (v) => !Object.keys(optionDefaults || {}).includes(v.Type)
        ) || []),
        // ...Object.keys(optionDefaults || {})
        //   .filter((key) => optionDefaults[key])
        //   .map((key) => ({
        //     Type: key,
        //     ...(typeof optionDefaults[key] === "object"
        //       ? optionDefaults[key]
        //       : {
        //           // if optionDefaults[key] is a string, use it as Value)
        //           Value: optionDefaults[key],
        //         }),
        //   })),
        ...(Object.keys(optionDefaults || {})
          .filter((key) => optionDefaults?.[key] !== null)
          .reduce((acc: Option[], key) => {
            // if optionDefaults[key] is an object
            if (typeof optionDefaults?.[key] === "object") {
              if (Array.isArray(optionDefaults[key])) {
                // if it's an array, spread it
                return [
                  ...acc,
                  ...optionDefaults[key].map((v: any) => ({ Type: key, ...v })),
                ];
              }
              // else spread the object
              return [...acc, { Type: key, ...optionDefaults[key] }];
            }

            // if optionDefaults[key] is a string, use it as Value)
            return [
              ...acc,
              {
                Type: key,
                Value: optionDefaults?.[key],
              },
            ];
          }, []) || []),
      ],
    });
  };

  const isAvailable = (item: any) =>
    item &&
    (!item.ifOptionTypeHas ||
      !item.ifOptionTypeHas.find(
        (cond: any) =>
          !condition.Options?.find(
            (opt: Option) =>
              opt.Type === cond.optionType && cond.values.includes(opt.Value)
          )
      )) &&
    (!item.ifPropertyHas ||
      !item.ifPropertyHas.find(
        (cond: any) => !cond.values.includes(condition[cond.field])
      ));

  if (isAvailable(input)) {
    return (
      <div className={className}>
        {input.type === "property" && (
          <div className={`conditions-${input.type}-${input.field}`}>
            <Input
              isSummary={!setCondition}
              maxLength={510}
              {...input.props}
              onCreateOption={(value) => {
                propertyChangeHandler([input.props.onCreateOption(value)]);
              }}
              options={
                input.props.options
                  // filter available operators
                  ?.filter((opt: any) => isAvailable(opt)) ?? []
              }
              value={[condition[input.field]]}
              onChange={(values) => propertyChangeHandler(values)}
            />
          </div>
        )}

        {input.field === "json:dataKey" && (
          <div className={`conditions-${input.type}-${input.field}`}>
            <KeyInput
              label="JSON key"
              isSummary={!setCondition}
              value={
                condition.Options?.find((o: Option) => o.Type === "json:dataKey")
                  ?.Value ?? ""
              }
              onChange={(value) =>
                setCondition?.({
                  ...condition,
                  Options: [
                    ...(condition.Options?.filter(
                      (o) => o.Type !== "json:dataKey"
                    ) ?? []),
                    { Value: value, Type: "json:dataKey" },
                  ],
                })
              }
            />
          </div>
        )}

        {input.type === "option" && input.field !== "json:dataKey" && (
          <div className={`conditions-${input.type}-${input.field}`}>
            <OptionInput
              isSummary={!setCondition}
              key={`${condition.UUID}-${input.props.optionType}-${input.props.uniqueKey}`}
              maxLength={510}
              {...input.props}
              condition={condition}
              optionType={input.field}
              options={input.props.options}
              value={condition.Options}
              operator={condition.Operator}
              isDataCategory={input.isDataCategory}
              onChange={(values) => optionChangeHandler({ values })}
            />
          </div>
        )}

        {input.type === "compound" && input.field !== "json:dataKey" && (
          <div className={`conditions-${input.type}-${input.field}`}>
            <CompoundInput
              isConditionInput={isConditionInput}
              isSummary={!setCondition}
              key={`${condition.UUID}-${input.props.optionType}-${input.props.uniqueKey}`}
              maxLength={510}
              {...input.props}
              defaultValue={input.defaultValue}
              condition={condition}
              optionType={input.field}
              options={input.props.options}
              values={(condition.Options || []).filter(
                (o: any) =>
                  o.Type === input.field &&
                  (input.props.keyValue ? o.Key === input.props.keyValue : true)
              )}
              operator={condition.Operator}
              isDataCategory={input.isDataCategory}
              onChange={(values: any) =>
                optionChangeHandler({
                  values: [
                    ...(condition.Options?.filter(
                      (o: any) =>
                        !(
                          o.Type === input.field &&
                          (input.props.keyValue
                            ? o.Key === input.props.keyValue
                            : true)
                        )
                    ) ?? []),
                    ...values,
                  ],
                  // input,
                })
              }
            />
          </div>
        )}

        {input.type === "heading" && <h5>{input.label}</h5>}

        {input.type === "html" && input.html}

        {input.children}

        <div className="text-danger mt-1">
          <Errors
            // @ts-expect-error - Errors component filterFn prop accepts parameters
            filterFn={(e: any) =>
              (e.action?.UUID === condition.UUID ||
                e.condition?.UUID === condition.UUID) &&
              e.error?.field === input.field
            }
            linkDisabled
          />
        </div>
      </div>
    );
  }
}
